import { Link } from "react-router-dom";
import { TID } from "@/constants/testIds";
import {
  ArrowRight, ShieldCheck, Fingerprint, Eye, Users, BookOpen,
  MessageSquare, Wallet, Network, FileText, BadgeCheck, IdCard,
  Gauge, Building2, Dice5, HeartPulse, Landmark, Signal, Stethoscope,
  Layers, Workflow, CheckCircle2, Circle,
} from "lucide-react";

const ALTARA_LOGO = "https://customer-assets.emergentagent.com/job_413a4d1d-7808-4808-afdb-26c6e8aa4d99/artifacts/mgbxngl3_Altara%20New.png";

// ─── The Shift comparison rows ───────────────────────────────
const SHIFT_ROWS = [
  ["Identity Verification", "Digital Trust"],
  ["KYC", "Trust Orchestration"],
  ["Compliance", "Outcome Governance"],
  ["One-off onboarding", "Continuous Trust"],
  ["Static customer records", "Living Consumer Intelligence"],
  ["Policies", "Evidence"],
  ["Reactive controls", "Continuous Monitoring"],
];

// ─── Systems institutions already have ───────────────────────
const EXISTING_SYSTEMS = [
  "Customer onboarding", "Identity verification", "Fraud detection",
  "CRM", "Learning", "Customer support",
  "AI", "Complaints", "Core business operations",
];

// ─── Questions those systems don't answer ────────────────────
const UNANSWERED_QUESTIONS = [
  "Did the consumer understand?",
  "Was appropriate support provided?",
  "Were interventions effective?",
  "Can you demonstrate fair outcomes?",
  "Where is the evidence?",
];

// ─── The 10-step Trust flow ──────────────────────────────────
const TRUST_FLOW = [
  "Customer Onboarding",
  "Trust Requests",
  "Identity & Verification",
  "Learning & Consumer Support",
  "Trust Evidence",
  "Trust Credentials",
  "Consumer Trust Wallet",
  "Outcome Governance",
  "Consumer Intelligence",
  "Fair Consumer Outcomes",
];

// ─── The 7 Altara platform capabilities ──────────────────────
const CAPABILITIES = [
  {
    icon: Wallet, title: "Consumer Trust Wallet",
    desc: "A secure digital wallet where consumers manage Trust Credentials, Trust Requests and their Trust Wallet.",
  },
  {
    icon: Workflow, title: "Trust Requests",
    desc: "A universal orchestration engine for identity verification, consent, affordability, learning, KYC, gaming and future trust workflows.",
  },
  {
    icon: Network, title: "Trust Utility",
    desc: "A provider-neutral orchestration layer connecting identity providers, KYC vendors, affordability services, learning platforms and AI services.",
  },
  {
    icon: FileText, title: "Trust Evidence",
    desc: "Immutable, explainable evidence generated automatically throughout every workflow.",
  },
  {
    icon: BadgeCheck, title: "Trust Credentials",
    desc: "Reusable digital credentials that consumers own and institutions can request, verify and rely upon.",
  },
  {
    icon: IdCard, title: "Trust Wallet",
    desc: "A portable, evidence-backed profile representing a consumer's digital trust relationship.",
  },
  {
    icon: Gauge, title: "Outcome Governance",
    desc: "Continuous monitoring and explainable evidence demonstrating fair consumer outcomes.",
  },
];

// ─── Outcomes institutions must evidence ─────────────────────
const OUTCOMES_TO_EVIDENCE = [
  { icon: BookOpen, text: "Understood important decisions." },
  { icon: Users, text: "Received appropriate support." },
  { icon: ShieldCheck, text: "Were protected from foreseeable harm." },
  { icon: CheckCircle2, text: "Experienced consistently fair outcomes." },
  { icon: MessageSquare, text: "Can trust AI-assisted services." },
  { icon: Fingerprint, text: "Can engage confidently in digital channels." },
];

// ─── Industries ──────────────────────────────────────────────
const INDUSTRIES = [
  { icon: Building2, label: "Financial Services" },
  { icon: Dice5, label: "Responsible Gaming" },
  { icon: HeartPulse, label: "Insurance" },
  { icon: Stethoscope, label: "Healthcare" },
  { icon: Landmark, label: "Government" },
  { icon: Signal, label: "Telecommunications" },
];

// ─── Interactions that build trust (philosophy) ──────────────
const PHILOSOPHY_INTERACTIONS = [
  "Every verification.",
  "Every learning experience.",
  "Every consent.",
  "Every support interaction.",
  "Every intervention.",
  "Every outcome.",
];


export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ─── NAV ─────────────────────────────────────────── */}
      <header className="border-b border-border bg-white/85 backdrop-blur sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={ALTARA_LOGO} alt="Altara" className="h-8 w-auto" />
            <div className="hidden md:flex items-center gap-2 pl-3 border-l border-border">
              <span className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Digital Trust Infrastructure</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="#shift" className="text-xs text-slate-600 hover:text-altara-deep">The Shift</a>
            <a href="#challenge" className="text-xs text-slate-600 hover:text-altara-deep">Challenge</a>
            <a href="#platform" className="text-xs text-slate-600 hover:text-altara-deep">Platform</a>
            <a href="#how" className="text-xs text-slate-600 hover:text-altara-deep">How it works</a>
            <a href="#industries" className="text-xs text-slate-600 hover:text-altara-deep">Industries</a>
            <a href="#philosophy" className="text-xs text-slate-600 hover:text-altara-deep">Philosophy</a>
            <Link to="/demos" data-testid="landing-nav-demos" className="text-xs text-slate-600 hover:text-altara-deep inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Live demos
            </Link>
            <Link
              to="/login"
              data-testid={TID.landingCtaLogin}
              className="inline-flex items-center gap-1.5 bg-altara-deep hover:bg-altara-ink text-white text-xs px-3.5 py-2 rounded-sm transition-colors"
            >
              Sign in <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </header>

      {/* ─── HERO ────────────────────────────────────────── */}
      <section data-testid={TID.landingHero} className="relative altara-noise">
        <div className="absolute inset-0 altara-grid-pattern opacity-[0.35] pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 pb-24 relative">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-slate-200 bg-white rounded-sm text-[10px] uppercase tracking-[0.22em] text-slate-600 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-altara-teal animate-pulse-subtle" />
              Digital Trust · Consumer Intelligence · Outcome Governance
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] text-altara-ink">
              Building the Infrastructure for<br />
              <span className="altara-gradient-text">Digital Trust.</span>
            </h1>
            <p className="mt-6 font-display text-lg md:text-xl text-altara-deep font-medium leading-snug max-w-3xl">
              Because every digital interaction creates a trust obligation.
            </p>
            <div className="mt-6 space-y-4 text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl">
              <p>
                Financial services, gaming, insurance and other regulated industries are entering a new era.
              </p>
              <p>
                Consumers increasingly interact with AI, automation and digital-first experiences. Institutions are
                no longer judged only by whether they verified identity or complied with regulations, but by whether
                they can consistently demonstrate that consumers experienced fair, informed and explainable outcomes.
              </p>
              <p>
                Altara is the <strong className="text-altara-deep">Digital Trust Infrastructure</strong> that enables
                organisations to orchestrate trust, generate evidence and continuously demonstrate fair consumer outcomes.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/demos" data-testid="landing-cta-request-demo" className="inline-flex items-center gap-2 bg-altara-deep hover:bg-altara-ink text-white text-sm px-5 py-2.5 rounded-sm transition-colors">
                Request a Demo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="landing-cta-explore" className="inline-flex items-center gap-2 border border-slate-300 hover:border-altara-deep hover:text-altara-deep text-slate-700 text-sm px-5 py-2.5 rounded-sm transition-colors">
                Explore the Platform
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── THE SHIFT ───────────────────────────────────── */}
      <section id="shift" className="border-y border-border bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-2">The Shift</div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-altara-ink leading-tight">
              The Next Generation of<br />
              <span className="altara-gradient-text">Trust Infrastructure.</span>
            </h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-slate-600 leading-relaxed">
              <p>
                For decades, organisations invested in infrastructure to verify identity.
              </p>
              <p className="text-altara-deep font-medium">
                The next decade requires infrastructure that continuously builds, measures and demonstrates trust.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-slate-200 rounded-sm overflow-hidden bg-white">
              <div className="grid grid-cols-2 border-b border-slate-200">
                <div className="px-5 py-3 border-r border-slate-200">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Yesterday</div>
                </div>
                <div className="px-5 py-3 bg-altara-teal/5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Tomorrow</div>
                </div>
              </div>
              {SHIFT_ROWS.map(([yesterday, tomorrow], i) => (
                <div key={yesterday} className={`grid grid-cols-2 ${i !== SHIFT_ROWS.length - 1 ? "border-b border-slate-100" : ""}`}>
                  <div className="px-5 py-3.5 border-r border-slate-100 flex items-center gap-2">
                    <Circle className="w-3 h-3 text-slate-300 shrink-0" strokeWidth={2} />
                    <span className="text-sm text-slate-500">{yesterday}</span>
                  </div>
                  <div className="px-5 py-3.5 flex items-center gap-2 bg-altara-teal/[0.03]">
                    <CheckCircle2 className="w-3.5 h-3.5 text-altara-teal shrink-0" strokeWidth={2} />
                    <span className="text-sm text-altara-ink font-medium">{tomorrow}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── THE CHALLENGE ───────────────────────────────── */}
      <section id="challenge" className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
        <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-2">The Challenge</div>
        <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-altara-ink leading-tight max-w-3xl">
          Trust Cannot Be Assumed.
        </h2>

        <div className="mt-10 grid lg:grid-cols-2 gap-6">
          {/* What institutions already have */}
          <div className="border border-slate-200 rounded-sm bg-white p-6">
            <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-5">
              Organisations already have systems for:
            </p>
            <div className="grid grid-cols-2 gap-2">
              {EXISTING_SYSTEMS.map((system) => (
                <div key={system} className="flex items-center gap-2 py-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-slate-400 shrink-0" strokeWidth={2} />
                  <span className="text-sm text-slate-600">{system}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Questions those systems can't answer */}
          <div className="border border-altara-teal/40 rounded-sm bg-altara-teal/[0.04] p-6">
            <p className="text-sm md:text-base text-altara-deep font-medium leading-relaxed mb-5">
              Yet none of these systems answer the questions regulators, boards and consumers increasingly ask:
            </p>
            <ul className="space-y-3">
              {UNANSWERED_QUESTIONS.map((q) => (
                <li key={q} className="flex items-start gap-3 pb-3 border-b border-altara-teal/20 last:border-0 last:pb-0">
                  <span className="font-mono text-[10px] text-altara-teal mt-1 shrink-0">Q.</span>
                  <span className="text-sm md:text-base text-altara-ink font-medium">{q}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 max-w-3xl">
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            Trust is no longer measured by what organisations intend.
          </p>
          <p className="mt-2 text-base md:text-lg text-altara-deep font-medium leading-relaxed">
            It is measured by what consumers experience.
          </p>
        </div>
      </section>

      {/* ─── WHAT IS ALTARA? ─────────────────────────────── */}
      <section id="what" className="border-y border-border bg-slate-50 relative overflow-hidden">
        <div className="absolute inset-0 altara-grid-pattern opacity-[0.25] pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid lg:grid-cols-12 gap-10 relative">
          <div className="lg:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-2">What is Altara?</div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-altara-ink leading-tight">
              The Digital Trust<br />
              <span className="altara-gradient-text">Infrastructure Layer.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4 text-sm md:text-base text-slate-600 leading-relaxed">
            <p>
              Altara sits above your existing technology ecosystem.
            </p>
            <p>
              <span className="text-altara-deep font-semibold">It does not replace your systems.</span>{" "}
              <span className="text-altara-deep font-semibold">It connects them.</span>
            </p>
            <p>
              By orchestrating identity, verification, learning, consent, affordability, customer interactions and
              AI-driven services, Altara creates a continuously evolving understanding of both consumers and
              institutional outcomes.
            </p>

            {/* Layered diagram */}
            <div className="mt-8 border border-slate-200 rounded-sm bg-white p-5 space-y-2.5">
              <div className="flex items-center gap-3 border-2 border-altara-teal bg-altara-teal/5 rounded-sm px-4 py-3">
                <Layers className="w-4 h-4 text-altara-teal shrink-0" strokeWidth={2} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Altara — Digital Trust Infrastructure</div>
                  <div className="text-xs text-slate-600">Orchestration · Evidence · Outcomes</div>
                </div>
              </div>
              <div className="pl-2 pt-1 text-center text-[10px] uppercase tracking-[0.22em] text-slate-400">connects</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {["CRM", "Core banking", "Fraud", "Learning", "Support", "AI"].map((s) => (
                  <div key={s} className="border border-slate-200 bg-slate-50 rounded-sm px-3 py-2 text-xs text-slate-600 text-center">
                    {s}
                  </div>
                ))}
              </div>
            </div>

            <p className="pt-2 text-altara-deep font-medium">
              Every interaction strengthens your evidence of trust.
            </p>
          </div>
        </div>
      </section>

      {/* ─── HOW ALTARA WORKS ────────────────────────────── */}
      <section id="how" className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
        <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-2">How Altara Works</div>
        <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-altara-ink leading-tight max-w-3xl">
          Every Trust Interaction<br />
          <span className="altara-gradient-text">Creates Evidence.</span>
        </h2>

        {/* Flow — vertical rail on the left, connected steps on the right */}
        <div className="mt-10 relative">
          <div className="grid gap-2">
            {TRUST_FLOW.map((step, i) => {
              const isLast = i === TRUST_FLOW.length - 1;
              const isOutcome = isLast;
              return (
                <div key={step} className="flex items-stretch gap-4">
                  {/* Numbered rail */}
                  <div className="flex flex-col items-center shrink-0">
                    <div className={`w-9 h-9 rounded-sm flex items-center justify-center font-mono text-[11px] font-semibold ${isOutcome ? "bg-altara-teal text-white" : "bg-altara-deep text-white"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    {!isLast && <div className="flex-1 w-px bg-slate-300 my-1" />}
                  </div>
                  {/* Step card */}
                  <div className={`flex-1 border rounded-sm px-4 py-3.5 ${isOutcome ? "border-altara-teal bg-altara-teal/[0.04]" : "border-slate-200 bg-white"}`}>
                    <div className={`font-display text-base font-semibold ${isOutcome ? "text-altara-teal" : "text-altara-ink"}`}>
                      {step}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="mt-10 max-w-3xl text-base md:text-lg text-slate-600 leading-relaxed">
          Every interaction contributes to a continuously evolving picture of{" "}
          <strong className="text-altara-deep">consumer trust</strong>.
        </p>
      </section>

      {/* ─── THE ALTARA PLATFORM ─────────────────────────── */}
      <section id="platform" className="border-y border-border bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-2">The Altara Platform</div>
          <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-altara-ink leading-tight max-w-3xl">
            One Platform.<br />
            <span className="altara-gradient-text">Multiple Trust Capabilities.</span>
          </h2>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CAPABILITIES.map(({ icon: Icon, title, desc }, i) => (
              <div key={title} className="group border border-slate-200 rounded-sm bg-white p-5 hover:border-altara-teal transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-mono text-[10px] text-altara-teal font-semibold">{String(i + 1).padStart(2, "0")}</span>
                  <div className="w-9 h-9 rounded-sm border border-slate-200 bg-slate-50 group-hover:bg-teal-50 group-hover:border-teal-200 flex items-center justify-center transition-colors">
                    <Icon className="w-4 h-4 text-altara-deep group-hover:text-altara-teal transition-colors" strokeWidth={1.75} />
                  </div>
                </div>
                <div className="font-display font-semibold text-base text-altara-ink mb-1.5">{title}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── WHY IT MATTERS ──────────────────────────────── */}
      <section id="why" className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-2">Why It Matters</div>
            <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-altara-ink leading-tight">
              Digital Trust Is Becoming<br />
              <span className="altara-gradient-text">Critical Infrastructure.</span>
            </h2>
            <div className="mt-6 space-y-4 text-sm md:text-base text-slate-600 leading-relaxed">
              <p>Identity verification alone is no longer enough.</p>
              <p className="text-altara-deep font-medium">
                Institutions must increasingly demonstrate that consumers:
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-3">
              {OUTCOMES_TO_EVIDENCE.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3 border border-slate-200 rounded-sm p-4 bg-white">
                  <Icon className="w-4 h-4 text-altara-teal mt-0.5 shrink-0" strokeWidth={1.75} />
                  <span className="text-sm text-slate-700">{text}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm md:text-base text-slate-600 leading-relaxed">
              Altara enables organisations to evidence each of these outcomes through a{" "}
              <strong className="text-altara-deep">single Digital Trust Infrastructure</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* ─── INDUSTRIES ──────────────────────────────────── */}
      <section id="industries" className="border-y border-border bg-slate-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-2">Industries</div>
          <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-altara-ink leading-tight max-w-3xl">
            One Platform.<br />
            <span className="altara-gradient-text">Multiple Industries.</span>
          </h2>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {INDUSTRIES.map(({ icon: Icon, label }) => (
              <div key={label} className="border border-slate-200 rounded-sm bg-white p-5 hover:border-altara-teal transition-colors">
                <Icon className="w-5 h-5 text-altara-deep mb-3" strokeWidth={1.75} />
                <div className="font-display text-sm font-semibold text-altara-ink leading-tight">{label}</div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-sm md:text-base text-slate-600 leading-relaxed">
            <strong className="text-altara-deep">Any regulated industry</strong> where digital trust matters.
          </p>
        </div>
      </section>

      {/* ─── PHILOSOPHY ──────────────────────────────────── */}
      <section id="philosophy" className="relative overflow-hidden">
        <div className="absolute inset-0 altara-gradient-bg opacity-[0.03] pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 relative">
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-2">Our Philosophy</div>
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="font-display font-bold text-3xl lg:text-5xl tracking-tight text-altara-ink leading-tight">
                Trust Is Built,<br />
                <span className="altara-gradient-text">Not Assumed.</span>
              </h2>
              <div className="mt-6 space-y-4 text-sm md:text-base text-slate-600 leading-relaxed max-w-lg">
                <p>Identity establishes who a person is.</p>
                <p className="text-altara-deep font-medium">
                  Trust is built through every interaction that follows.
                </p>
              </div>
            </div>

            <div className="border border-slate-200 rounded-sm bg-white p-6 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-3">The interactions that build trust</div>
              <ul className="divide-y divide-slate-100">
                {PHILOSOPHY_INTERACTIONS.map((line) => (
                  <li key={line} className="py-2.5 flex items-center gap-3">
                    <Eye className="w-3.5 h-3.5 text-altara-teal shrink-0" strokeWidth={2} />
                    <span className="text-sm md:text-base text-slate-700">{line}</span>
                  </li>
                ))}
              </ul>
              <p className="pt-4 text-sm md:text-base text-slate-600 leading-relaxed border-t border-slate-100 mt-2">
                Together, these create the evidence that organisations need to demonstrate trust in the digital economy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CLOSING CTA ─────────────────────────────────── */}
      <section id="cta" className="border-y border-border bg-altara-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 altara-grid-pattern opacity-[0.08] pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 relative">
          <div className="max-w-3xl">
            <div className="space-y-2 text-sm md:text-base text-white/70 leading-relaxed">
              <p>The future of regulation is <span className="text-white font-medium">evidence</span>.</p>
              <p>The future of customer experience is <span className="text-white font-medium">trust</span>.</p>
              <p>The future of digital services is <span className="text-white font-medium">explainable</span>.</p>
            </div>
            <p className="mt-6 text-lg md:text-xl text-white/80 leading-relaxed">
              Altara enables organisations to build all three.
            </p>

            <h2 className="mt-10 font-display font-bold text-3xl md:text-5xl tracking-tighter leading-[1.05]">
              Build Digital Trust.<br />
              <span className="altara-gradient-text">Demonstrate Fair Consumer Outcomes.</span>
            </h2>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/demos" data-testid="landing-cta-request-demo-footer" className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-altara-ink text-sm px-5 py-2.5 rounded-sm transition-colors font-medium">
                Request a Demo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" data-testid="landing-cta-explore-footer" className="inline-flex items-center gap-2 border border-white/30 hover:border-white/70 text-white text-sm px-5 py-2.5 rounded-sm transition-colors">
                Explore the Platform
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ──────────────────────────────────────── */}
      <footer className="border-t border-border bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <img src={ALTARA_LOGO} alt="Altara" className="h-10 w-auto mb-4" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-2">Digital Trust Infrastructure</div>
              <p className="font-display text-lg text-altara-ink font-semibold leading-snug">
                Building the Infrastructure for Digital Trust.
              </p>
              <p className="mt-3 text-xs text-slate-600 leading-relaxed max-w-md">
                Altara is the Digital Trust Infrastructure that enables organisations to orchestrate trust, generate
                evidence and continuously demonstrate fair consumer outcomes.
              </p>
              <p className="mt-3 text-xs text-altara-teal font-semibold">
                Digital Trust · Consumer Intelligence · Outcome Governance
              </p>
            </div>
            <div className="md:col-span-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-3">Ecosystem</div>
              <ul className="space-y-1.5 text-xs text-slate-700">
                <li><a href="https://www.altaracore.ai" target="_blank" rel="noreferrer" className="hover:text-altara-deep">Altara Core</a></li>
                <li><span className="text-altara-deep font-medium">Altara Consumer Intelligence</span></li>
                <li className="text-slate-400">Altara Conduct Operating System (soon)</li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-3">Governance</div>
              <ul className="space-y-1.5 text-xs text-slate-700">
                <li>Digital Trust</li>
                <li>AI Governance</li>
                <li>Fair Consumer Outcomes</li>
                <li>IOSCO TechSprint · 2026</li>
                <li>FSCA-Mentored</li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-3">Company</div>
              <ul className="space-y-1.5 text-xs text-slate-700">
                <li>Part of the Navigate Group (Pty) Ltd</li>
                <li>Registered in South Africa</li>
                <li>Johannesburg, South Africa</li>
                <li><a href="https://www.altaracore.ai" target="_blank" rel="noreferrer" className="hover:text-altara-deep">www.altaracore.ai</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="border-t border-border">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex flex-wrap items-center justify-between gap-3">
            <span className="text-[11px] text-slate-500">© {new Date().getFullYear()} Altara. Trust by design. Outcomes by proof.</span>
            <span className="text-[11px] text-altara-teal font-medium">Digital Trust Infrastructure</span>
            <span className="text-[11px] font-mono text-slate-400">v0.2.0</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
